import { FileUpload } from '@mui/icons-material';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DragEvent as ReactDragEvent, ReactNode, useEffect, useRef, useState } from 'react';

function isFileDrag(e: { dataTransfer: DataTransfer | null }) {
  // Text and link drags from inside the page also fire drag events; only react to files
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}

/**
 * Stops a file dropped outside any drop zone from making the browser navigate
 * away to open it. Zones call preventDefault first (React handlers run before
 * window listeners), so anything still unhandled here missed every zone.
 */
function useWindowFileDropGuard() {
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (e.defaultPrevented || !isFileDrag(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none'
    }
    const onDrop = (e: DragEvent) => {
      if (e.defaultPrevented || !isFileDrag(e)) return
      e.preventDefault()
    }
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
    }
  }, [])
}

/**
 * Accepts files dragged in from the desktop.
 *
 * - `inline`: a dashed box that highlights while files are dragged over it.
 * - `overlay`: wraps existing content and covers it with a "drop here" panel
 *   while files are dragged over it.
 */
export function FileDropZone({ onFiles, disabled = false, variant, overlayText = 'Drop files to upload', children, sx }: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  variant: 'inline' | 'overlay';
  overlayText?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  const [isDragOver, setIsDragOver] = useState(false)
  // dragenter/dragleave fire for every child crossed, so count depth rather
  // than trusting relatedTarget (which Safari leaves null)
  const dragDepth = useRef(0)

  useWindowFileDropGuard()

  useEffect(() => {
    if (disabled) {
      dragDepth.current = 0
      setIsDragOver(false)
    }
  }, [disabled])

  const accepts = (e: ReactDragEvent) => !disabled && isFileDrag(e)

  const handlers = {
    onDragEnter: (e: ReactDragEvent) => {
      if (!accepts(e)) return
      e.preventDefault()
      dragDepth.current += 1
      setIsDragOver(true)
    },
    onDragOver: (e: ReactDragEvent) => {
      if (!accepts(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave: (e: ReactDragEvent) => {
      if (!accepts(e)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setIsDragOver(false)
    },
    onDrop: (e: ReactDragEvent) => {
      if (!accepts(e)) return
      e.preventDefault()
      dragDepth.current = 0
      setIsDragOver(false)
      const files = Array.from(e.dataTransfer.files)
      if (files.length) onFiles(files)
    },
  }

  if (variant === 'inline') {
    return (
      <Box
        {...handlers}
        data-testid="file-drop-zone"
        data-dragover={isDragOver}
        sx={[
          {
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: isDragOver ? 'action.hover' : 'transparent',
            transition: 'border-color 0.2s, background-color 0.2s',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      {...handlers}
      data-testid="file-drop-zone"
      data-dragover={isDragOver}
      sx={[{ position: 'relative' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            // Let drag events reach the content underneath so the depth count stays balanced
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 'inherit',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
          }}
        >
          <FileUpload color="primary" fontSize="large" />
          <Typography variant="h6" color="primary">{overlayText}</Typography>
        </Box>
      )}
    </Box>
  )
}
